import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigationType, useSearchParams } from "react-router-dom";
import {
  LuArrowDownWideNarrow,
  LuArrowUpDown,
  LuCalendarClock,
  LuDatabase,
  LuFilter,
  LuPlus,
  LuSearch,
  LuUpload,
} from "react-icons/lu";
import { toast } from "react-toastify";

import ExportMenu from "../../components/importExport/ExportMenu";
import ImportModal from "../../components/importExport/ImportModal";
import MaterialFormModal from "../../components/materials/MaterialFormModal";
import MaterialList from "../../components/materials/MaterialList";
import MaterialStockEntryModal from "../../components/materialStocks/MaterialStockEntryModal";
import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import Pagination from "../../components/pagination";
import Button from "../../components/ui/Button";
import FilterSelect, { FilterOption } from "../../components/ui/FilterSelect";
import Input from "../../components/ui/Input";
import { sortLabelsOf } from "../../components/ui/SortableTableHead";
import cx from "../../components/ui/cx";
import {
  actionBarClass,
  cardClass,
  cardContentClass,
  cardHeaderClass,
  cardTitleClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  pageTitleClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchMaterialsListData,
  getEditMaterial,
  getMaterials,
  getTotalMaterials,
  setEditMaterial,
  toggleMaterialModal,
} from "../../features/materials/materialsSlice";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { DELETE, POST, PUT } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import {
  IMaterialFormInput,
  MaterialDateBy,
  MaterialSortBy,
  MaterialSortOrder,
  MaterialsProps,
} from "../../types/material";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "મટીરીયલ" },
];

const listKey = "materials";

// The dates filter the date a material was added, newest first, until the user picks otherwise.
const defaultSelects: {
  date_by: MaterialDateBy;
  sort_by: MaterialSortBy;
  sort_order: MaterialSortOrder;
} = {
  date_by: "created_at",
  sort_by: "id",
  sort_order: "desc",
};

const dateByOptions: FilterOption<MaterialDateBy>[] = [
  { value: "created_at", label: "બનાવ્યા તારીખ" },
  { value: "updated_at", label: "સુધાર્યા તારીખ" },
];

const sortByOptions: FilterOption<MaterialSortBy>[] = [
  { value: "id", label: "ID" },
  { value: "name", label: "મટીરીયલ નામ" },
  { value: "created_at", label: "બનાવ્યા તારીખ" },
  { value: "updated_at", label: "સુધાર્યા તારીખ" },
];

const sortLabels = sortLabelsOf(sortByOptions);

const sortOrderOptions: FilterOption<MaterialSortOrder>[] = [
  { value: "asc", label: "ચડતો ક્રમ" },
  { value: "desc", label: "ઉતરતો ક્રમ" },
];

const MaterialsPage = () => {
  const dispatch = useAppDispatch();
  const materials = useAppSelector(getMaterials);
  const totalMaterials = useAppSelector(getTotalMaterials);
  const editMaterial = useAppSelector(getEditMaterial);
  const loggingUser = useAppSelector(getLoggingUser);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const canCreate = hasPermission(
    loggingUser?.permissions,
    permission.materialsCreate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.materialsReport
  );
  // The import updates the materials a file names by a saved name only for a user who may update materials.
  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.materialsUpdate
  );
  // The list asks for each material's bars in stock only for a user who may see them.
  const canStock = hasPermission(
    loggingUser?.permissions,
    permission.materialStocksRead
  );

  interface FilterFormInput {
    name: string;
    fromDate: string;
    toDate: string;
    date_by: MaterialDateBy;
    sort_by: MaterialSortBy;
    sort_order: MaterialSortOrder;
  }

  const emptyFilters: FilterFormInput = {
    name: "",
    fromDate: "",
    toDate: "",
    ...defaultSelects,
  };

  // Opened from a material order/purchase card: the list starts searched to that material. Coming back to it (from
  // a stock page, say) keeps the page and filters the user left it with instead.
  const [searchParams] = useSearchParams();
  const searchParam = searchParams.get("search") || "";
  const navigationType = useNavigationType();
  const isSearchLink =
    !!searchParam && !(navigationType === "POP" && savedList.filters);

  // The filters the list starts with: the link's search, or those in use when the user last left the list.
  const savedFilters: FilterFormInput = isSearchLink
    ? { ...emptyFilters, name: searchParam }
    : savedList.filters || emptyFilters;

  const { handleSubmit, register, reset, getValues, setValue, watch } =
    useForm<FilterFormInput>({ defaultValues: savedFilters });

  const [page, setPage] = useState<number>(
    isSearchLink ? 1 : savedList.page || 1
  );
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showFilter, setShowFilter] = useState<boolean>(
    isSearchLink || !!savedList.showFilter
  );
  const [showImport, setShowImport] = useState(false);
  // The order the list on screen was loaded in, which the table headers show. The filter's selects may hold an
  // order that isn't applied yet.
  const [appliedSort, setAppliedSort] = useState<{
    sortBy: MaterialSortBy;
    sortOrder: MaterialSortOrder;
  }>({ sortBy: savedFilters.sort_by, sortOrder: savedFilters.sort_order });
  // The material whose stock popup (ઉમેરો / બાદ) is open.
  const [stockMaterial, setStockMaterial] = useState<MaterialsProps | null>(
    null
  );

  const fetchId = useRef(0);

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleShowMaterialForm = async () => {
    await dispatch(setEditMaterial(null));
    dispatch(toggleMaterialModal(true));
  };

  const handleSaveMaterial = async (data: IMaterialFormInput) => {
    let saved = false;

    // No sizes: the server gives a new material its one 3660 size and leaves a saved one's sizes as they are.
    const payload = {
      name: data.name,
      description: data.description,
      image: data.image || null,
    };

    try {
      if (editMaterial && editMaterial.id) {
        await PUT(`/v1/admin/materials/${editMaterial.id}`, payload)
          .then(async (response: any) => {
            if (response.data.status === "success") {
              saved = true;

              await dispatch(setEditMaterial(null));

              if (page === 1) {
                fetchMaterialsList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      } else {
        await POST("/v1/admin/materials", payload)
          .then(async (response: any) => {
            if (response.data.status === "success") {
              saved = true;

              if (page === 1) {
                fetchMaterialsList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      }
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    return saved;
  };

  const handleDeleteMaterial = async (id: number) => {
    setIsLoading(true);

    try {
      await DELETE(`/v1/admin/materials/${id}`)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber = materials.length > 1 ? page : page - 1;
            if (page === pageNumber || pageNumber < 1) {
              fetchMaterialsList(page, pageSize);
            } else {
              setPage(pageNumber);
            }

            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
          } else {
            setIsLoading(false);

            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          setIsLoading(false);

          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      setIsLoading(false);

      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  // A new filter or import starts again from the first page.
  const showFirstPage = () => {
    if (page === 1) {
      fetchMaterialsList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleOnFilterSubmit = async () => {
    showFirstPage();
  };

  const handleClearFilter = () => {
    reset(emptyFilters);
    showFirstPage();
  };

  // Filter values shared by the list and the export.
  const filterParams = () => {
    const data = getValues();

    return {
      search: data?.name || null,
      fromDate: data?.fromDate || null,
      toDate: data?.toDate || null,
      date_by: data.date_by,
      sort_by: data.sort_by,
      sort_order: data.sort_order,
    };
  };

  const fetchMaterialsList = async (page: number, size: number) => {
    const currentFetch = ++fetchId.current;

    setIsLoading(true);

    const params = filterParams();

    dispatch(saveListState({ key: listKey, value: { filters: getValues() } }));
    setAppliedSort({ sortBy: params.sort_by, sortOrder: params.sort_order });

    dispatch(
      fetchMaterialsListData({
        page: page,
        size: size,
        ...params,
        ...(canStock ? { with_stock: 1 as const } : {}),
      })
    ).then((action: any) => {
      const result = action?.payload?.data;

      if (currentFetch === fetchId.current && result?.status !== "success") {
        toast.error(
          result?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }

      // A saved page that is now past the end (its rows deleted, say) moves to the last page.
      const lastPage = Math.max(
        1,
        Math.ceil((Number(result?.data?.total_records) || 0) / size)
      );

      if (
        currentFetch === fetchId.current &&
        page > 1 &&
        result?.status === "success" &&
        !result?.data?.records?.length &&
        lastPage < page
      ) {
        setPage(lastPage);
        return;
      }

      setIsLoading(false);
    });
  };

  // A table header sorts the list as the filter's ગોઠવો and ક્રમ do, and they show the same choice.
  const handleSort = (
    sortBy: MaterialSortBy,
    sortOrder: MaterialSortOrder
  ) => {
    setValue("sort_by", sortBy);
    setValue("sort_order", sortOrder);
    showFirstPage();
  };

  // Coming back to the list (from a record's page, say) shows the same page and filters.
  useEffect(() => {
    dispatch(
      saveListState({ key: listKey, value: { page, pageSize, showFilter } })
    );
  }, [dispatch, page, pageSize, showFilter]);

  useEffect(() => {
    fetchMaterialsList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>મટીરીયલ</h1>
      </div>

      <div className="space-y-4 md:space-y-6">
        <div className={actionBarClass}>
          <div
            className={cx(
              "grid w-full gap-2 sm:ml-auto sm:flex sm:w-auto",
              canCreate ? "grid-cols-2" : "grid-cols-1"
            )}
          >
            <Button
              variant="outline"
              onClick={() => setShowFilter(!showFilter)}
            >
              <LuFilter className="h-4 w-4" />
              ફિલ્ટર
            </Button>

            {canCreate && (
              <Button variant="outline" onClick={() => setShowImport(true)}>
                <LuUpload className="h-4 w-4" />
                ઇમ્પોર્ટ
              </Button>
            )}

            {canCreate && (
              <Button
                variant="primary"
                className="col-span-2"
                onClick={handleShowMaterialForm}
              >
                <LuPlus className="h-4 w-4" />
                નવું મટીરીયલ ઉમેરો
              </Button>
            )}
          </div>
        </div>

        {showFilter && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={`${cardTitleClass} text-lg`}>ફિલ્ટર</div>
            </div>

            <div className={cardContentClass}>
              <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
                <div className={filterGridClass}>
                  <div className="space-y-2">
                    <label className={filterLabelClass}>શોધો</label>
                    <div className="relative">
                      <LuSearch className={fieldIconClass} />
                      <Input
                        placeholder="નામ દ્વારા શોધો"
                        className={fieldInputClass()}
                        {...register("name")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ થી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        placeholder="તારીખ દાખલ કરો"
                        className={fieldInputClass()}
                        {...register("fromDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ સુધી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        placeholder="તારીખ દાખલ કરો"
                        className={fieldInputClass()}
                        {...register("toDate")}
                      />
                    </div>
                  </div>

                  <FilterSelect
                    label="તારીખ ફિલ્ટર"
                    icon={LuCalendarClock}
                    placeholder="તારીખ સિલેક્ટ કરો"
                    options={dateByOptions}
                    value={watch("date_by")}
                    onChange={(value) => value && setValue("date_by", value)}
                  />

                  <FilterSelect
                    label="ગોઠવો"
                    icon={LuArrowUpDown}
                    placeholder="ગોઠવણી સિલેક્ટ કરો"
                    options={sortByOptions}
                    value={watch("sort_by")}
                    onChange={(value) => value && setValue("sort_by", value)}
                  />

                  <FilterSelect
                    label="ક્રમ"
                    icon={LuArrowDownWideNarrow}
                    placeholder="ક્રમ સિલેક્ટ કરો"
                    options={sortOrderOptions}
                    value={watch("sort_order")}
                    onChange={(value) =>
                      value && setValue("sort_order", value)
                    }
                  />
                </div>

                <div className={filterFooterClass}>
                  <Button
                    type="button"
                    variant="outline"
                    className="max-sm:flex-1"
                    onClick={handleClearFilter}
                  >
                    ફિલ્ટર સાફ કરો
                  </Button>

                  {canReport && (
                    <ExportMenu
                      endpoint="/v1/admin/materials/export"
                      getParams={filterParams}
                    />
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="max-sm:flex-1"
                  >
                    ફિલ્ટર લાગુ કરો
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <MaterialList
          materials={materials}
          pageNumber={page}
          pageSize={pageSize}
          isLoading={isLoading}
          sortBy={appliedSort.sortBy}
          sortOrder={appliedSort.sortOrder}
          onSort={handleSort}
          sortLabels={sortLabels}
          handleDeleteMaterial={handleDeleteMaterial}
          onStockEntry={setStockMaterial}
        />

        {!isLoading && materials.length > 0 && (
          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalMaterials}
            paginate={paginate}
            currentPage={page}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="મટીરીયલ"
          />
        )}
      </div>

      <MaterialFormModal handleSaveMaterial={handleSaveMaterial} />

      {stockMaterial && (
        <MaterialStockEntryModal
          material={stockMaterial}
          entry={null}
          onClose={() => setStockMaterial(null)}
          onSaved={() => {
            setStockMaterial(null);
            fetchMaterialsList(page, pageSize);
          }}
        />
      )}

      <ImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        title="મટીરીયલ ઇમ્પોર્ટ કરો"
        endpoint="/v1/admin/materials"
        help={[
          "કોલમ: નામ, ડિસ્ક્રિપ્શન.",
          "દરેક મટીરીયલ માટે એક પંક્તિ બનાવો.",
          ...(canUpdate
            ? [
                "પહેલેથી ઉમેરેલા નામ વાળું મટીરીયલ ફરીથી નહીં બને, ફાઇલ મુજબ સુધારાશે: એક્સપોર્ટ કરેલી ફાઇલ માં ફેરફાર કરીને ઇમ્પોર્ટ કરો.",
                "સુધારાતા મટીરીયલ માં ખાલી ખાનું ખાલી સેવ થશે. ફોટો બદલાતો નથી.",
                "મટીરીયલ તેના નામ થી ઓળખાય છે: ફાઇલ માં બદલેલું નામ નવું મટીરીયલ બનાવે છે, તેથી નામ મટીરીયલ પેજ પર બદલો.",
              ]
            : ["પહેલેથી ઉમેરેલા નામ વાળું મટીરીયલ છોડી દેવામાં આવશે."]),
        ]}
        onImported={showFirstPage}
      />
    </AppLayout>
  );
};
export default MaterialsPage;
